// preloader
import * as THREE from 'three';
import { events } from '../constants'
import { layers } from '../constants'
import Tween from 'root/animator/tween/tween'

class Object1 {
  instance
  material
  geometry
  uniforms
  moreThan05 = true

  state = {
    alive: true,
    started: false
  }

  name = "Preloader"

  scene

  constructor( scene ) {
    this.scene = scene
    this.init( scene )
  }

  init( scene ) {
    let POINTS = 17;
    let points = new Float32Array(POINTS*2);
    let centers = new Float32Array(POINTS*2);

    this.updateSpiral(points, centers, 0, 0.5, true);
    this.geometry = new THREE.PlaneBufferGeometry(2, 2);
    this.uniforms = {
      iResolution: { value: {
        x: window.innerWidth * window.devicePixelRatio,
        y: window.innerHeight * window.devicePixelRatio
      }},
      iPoints: { value: points },
      iCenters: { value: centers },
      transparent: true,
    };
    this.material = new THREE.ShaderMaterial( {
      uniforms: this.uniforms,
      vertexShader: `
      varying vec2 vUv;
      void main()	{
        vUv = uv;
        gl_Position = vec4( position, 1.0 );
      }`,
      fragmentShader: `
      #define POINTS ${POINTS}
      #define RADIUS 0.03
      #define GLOW 1.5
  
      uniform vec2 iResolution;
      uniform vec2 iPoints[POINTS];
      uniform vec2 iCenters[POINTS];
      varying vec2 vUv;
      
      float dot2( in vec2 v ) { return dot(v,v); }
      vec2 sdBezier(vec2 pos, vec2 A, vec2 B, vec2 C) {    
        vec2 a = B - A;
        vec2 b = A - 2.0*B + C;
        vec2 c = a * 2.0;
        vec2 d = A - pos;
    
        float kk = 1.0 / dot(b,b);
        float kx = kk * dot(a,b);
        float ky = kk * (2.0*dot(a,a) + dot(d,b)) / 3.0;
        float kz = kk * dot(d,a);      
    
        vec2 res;
    
        float p = ky - kx*kx;
        float p3 = p*p*p;
        float q = kx*(2.0*kx*kx - 3.0*ky) + kz;
        float h = q*q + 4.0*p3;
    
        if(h >= 0.0) { 
          h = sqrt(h);
          vec2 x = (vec2(h, -h) - q) / 2.0;
          vec2 uv = sign(x)*pow(abs(x), vec2(1.0/3.0));
          float t = clamp(uv.x+uv.y-kx, 0.0, 1.0);
  
          // 1 root
          res = vec2(dot2(d+(c+b*t)*t),t);
          
          //res = vec2( dot2( pos-bezier(A,B,C,t)), t );
        } else {
          float z = sqrt(-p);
          float v = acos( q/(p*z*2.0) ) / 3.0;
          float m = cos(v);
          float n = sin(v)*1.732050808;
          vec3 t = clamp( vec3(m+m,-n-m,n-m)*z-kx, 0.0, 1.0);
          
          // 3 roots, but only need two
          float dis = dot2(d+(c+b*t.x)*t.x);
          res = vec2(dis,t.x);
  
          dis = dot2(d+(c+b*t.y)*t.y);
          if( dis<res.x ) res = vec2(dis,t.y );
        }
        
        res.x = sqrt(res.x);
        return res;
      }
  
      float map(vec2 pos){
        float d = 1e9;
        const int i = 0;
        for(int i = 0; i < POINTS - 2; i++) {
          vec2 f = sdBezier(pos, iCenters[i], iPoints[i + 1], iCenters[i + 1]);
          d = min(d, max(
            pow(f.x, 0.65) - .01,
            f.x + 0.3 * pow((float(POINTS - 2) - f.y - float(i)) / float(POINTS - 2), 1.85)
          ));
        }
        return d;
      }
      
      void main() {
        vec2 uv = (2. * gl_FragCoord.xy - iResolution.xy) / iResolution.y;
  
        float d = map(uv);
        vec3 col = vec3(.3, 1., .4) * min(pow(RADIUS/max(0., d), GLOW), 2.5);
        float f = length(uv - iCenters[POINTS - 2]);
        col = max(col, vec3(.8, .9, 1.) * min(pow(RADIUS/max(0., pow(f, 0.65)), GLOW), 5.));
  
        gl_FragColor = vec4(col, 1.0);
      }`
    } );
    this.instance = new THREE.Mesh(this.geometry, this.material);
    this.instance.name = this.name
    this.instance.layers.enable(layers.ENTIRE_SCENE)
    this.instance.visible = true
    scene.add(this.instance);
  }

  updateSpiral(points, centers, time, percent, init = false) {
    let count = points.length / 2;
    for (let i = 0; i < count; i++) {
      let t = 60 * percent - 8 * i / count * (1 + 2*percent);
      let l = .8*Math.pow(2.718, -0.05 * t);
      let x = l * Math.cos(t) + .1 * l * Math.cos(t*5/4 - 1);
      let y = l * Math.sin(t) + .1 * l * Math.sin(t*3/4 - 1);
      points[2*(count - i - 1)] = x;
      points[2*(count - i - 1) + 1] = y;
    }
    for (let i = 0; i < count - 1; i++) {
      centers[2 * i] = (points[2 * i] + points[2 * (i + 1)]) / 2;
      centers[2 * i + 1] = (points[2 * i + 1] + points[2 * (i + 1) + 1]) / 2;
    }
  }

  resize() {
    this.material.uniforms[ 'iResolution' ].value.x = window.innerWidth * window.devicePixelRatio;
    this.material.uniforms[ 'iResolution' ].value.y = window.innerHeight * window.devicePixelRatio;
  }

  render({ destroy = false }) {
    if (destroy && this.state.alive) {
      this.instance.visible = false
      this.scene.remove(this.instance)
      this.state.alive = false

      // костыль бля такой неприятный но че поделать...
      const hero = document.querySelector('.hero')
      if (hero) { setTimeout(() => { hero.classList.remove('-appearing') }, 1500) }
      // 
      document.dispatchEvent(new CustomEvent('loaderdestroy'))
    }

    // let time = performance.now() / 1000;
    // let percent = ((0.3 * performance.now()) % 1000) / 1000;


    if (!destroy && !this.state.started) {
      this.state.started = true
      Tween.to(0, 1, {
        duration: 5000,
        renderDelay: 10,
        onChange: function(context) {

          const time = performance.now() / 1000
          // const time = context.state.hole
          // console.log(time, context.state)
          const percent = context.value
          const fixedPercent = (Number((percent * 100).toFixed(0)) <= 100) ? (percent * 100).toFixed(0) : 100
          document.dispatchEvent(new CustomEvent(events.LOADING, { detail: { percent: fixedPercent } } ))


          let points = this.uniforms[ 'iPoints' ].value;
          let centers = this.uniforms[ 'iCenters' ].value;
          if (percent > .5) {
            this.updateSpiral(points, centers, 4 * time, percent);
            if (!this.moreThan05) this.moreThan05 = true;
          } else {
            this.updateSpiral(points, centers, 4 * time, percent, this.moreThan05);
            if (this.moreThan05) this.moreThan05 = false;
          }

        }.bind(this),
        onComplete: function() {
          document.dispatchEvent(new CustomEvent(events.LOADING, { detail: { percent: 100 } } ))
        }.bind(this)
      })
    }


  }
}

export default Object1